/**
 * Stage 73.20s — Multi-line Expense Claim Sheet (ESS).
 *
 * Employees enter a multi-day trip in a SINGLE grid: each row =
 * date + claim type + subcategory + amount + bill # + description.
 * Each row shows a live policy badge (within cap / exceeds by ₹X /
 * no policy). On submit, one reimbursement_claim is created per
 * row + an envelope `expense_claim_sheets` row tracks the trip.
 *
 * After submission the employee can download a print-ready PDF
 * (signature blocks for manager / HR / finance).
 */
import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus, Trash2, Loader2, RefreshCw, FileDown, Receipt,
  CheckCircle2, AlertTriangle, Upload, FileText, X,
  Search, Eye, Printer, FileSpreadsheet, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const DEFAULT_HEADS = [
  'Travel - Train', 'Travel - Flight', 'Travel - Cab/Taxi',
  'Hotel / Lodging', 'Food / Meals', 'Local Conveyance',
  'Telephone / Internet', 'Other',
];

const today = () => new Date().toISOString().slice(0, 10);

export default function ESSExpenseClaims() {
  const [policy, setPolicy] = useState({ has_policy: false, items: [] });
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [openAdvances, setOpenAdvances] = useState([]);
  const [claimType, setClaimType] = useState("direct_reimbursement"); // "against_advance" or "direct_reimbursement"
  const [selectedAdvanceId, setSelectedAdvanceId] = useState(null);
  const [trip, setTrip] = useState({
    title: '', trip_purpose: '', trip_from: '', trip_to: '',
    trip_start_date: '', trip_end_date: '',
  });
  const [lines, setLines] = useState(emptyRows(1));
  const [submitting, setSubmitting] = useState(false);
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSheetDetails, setSelectedSheetDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedDetailsLines, setExpandedDetailsLines] = useState([]);
  const [sheetLinesMap, setSheetLinesMap] = useState({});

  function emptyRows(n) {
    return Array.from({ length: n }, () => ({
      line_date: today(), head_code: '', subcategory: '',
      claim_amount: '', bill_number: '', description: '', proof_file_id: null,
      proof_filename: '',
    }));
  }

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pol, mine, advances] = await Promise.all([
        axios.get(`${API}/ess/reimbursement-policy`, { withCredentials: true }).catch(() => ({ data: { has_policy: false, items: [] } })),
        axios.get(`${API}/ess/expense-claims`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/advance-ledger/me/open-advances`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPolicy(pol.data || { has_policy: false, items: [] });
      setSheets(mine.data || []);
      setOpenAdvances(advances.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const headOptions = useMemo(() => {
    if (policy.has_policy && policy.items.length) {
      return policy.items.map(i => ({ value: i.category, label: i.subcategory ? `${i.category} (${i.subcategory})` : i.category, item: i }));
    }
    return DEFAULT_HEADS.map(h => ({ value: h, label: h, item: null }));
  }, [policy]);

  const policyForLine = (line) => {
    if (!policy.has_policy) return null;
    const matched = policy.items.find(i => i.category === line.head_code);
    if (!matched) return null;
    const amt = parseFloat(line.claim_amount) || 0;
    const cap = parseFloat(matched.limit_amount) || 0;
    const auto = matched.auto_approve_below;
    const exceedsBy = Math.max(amt - cap, 0);
    return {
      cap, unit: matched.limit_unit,
      autoThreshold: auto != null ? parseFloat(auto) : null,
      exceedsBy, within: exceedsBy === 0, amt,
      autoApprove: exceedsBy === 0 && auto != null && amt > 0 && amt <= parseFloat(auto),
    };
  };

  const total = useMemo(() =>
    lines.reduce((s, l) => s + (parseFloat(l.claim_amount) || 0), 0)
  , [lines]);

  const anyDeviation = useMemo(() =>
    lines.some(l => (policyForLine(l)?.exceedsBy || 0) > 0)
  , [lines, policy]);   // eslint-disable-line

  const addRow = () => setLines([...lines, ...emptyRows(1)]);
  const removeRow = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const uploadProof = async (idx, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/ess/kyc/upload-proof`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateLine(idx, { proof_file_id: res.data.file_id, proof_filename: res.data.filename });
      toast.success('Proof uploaded');
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const submitSheet = async () => {
    if (!trip.title.trim()) {
      toast.error('Give the claim sheet a title (e.g. "Chennai client visit")');
      return;
    }
    const cleaned = lines.filter(l => l.head_code && parseFloat(l.claim_amount) > 0);
    if (cleaned.length === 0) {
      toast.error('Add at least one line with category + amount');
      return;
    }
    const total = cleaned.reduce((sum, l) => sum + (parseFloat(l.claim_amount) || 0), 0);
    
    if (claimType === "against_advance") {
      if (!selectedAdvanceId) {
        toast.error('Please select an advance reference');
        return;
      }
      // Check total doesn't exceed remaining balance
      const selected = openAdvances.find(a => a.advance_id === selectedAdvanceId);
      if (selected) {
        const remaining = selected.pending_balance;
        if (total > remaining) {
          toast.error(`Claim amount (${fmtINR(total)}) exceeds available balance (${fmtINR(remaining)})`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ess/expense-claims`, {
        ...trip,
        trip_start_date: trip.trip_start_date || null,
        trip_end_date: trip.trip_end_date || null,
        advance_id: claimType === "against_advance" ? selectedAdvanceId : null,
        lines: cleaned.map(l => ({
          line_date: l.line_date,
          head_code: l.head_code,
          subcategory: l.subcategory || '',
          claim_amount: parseFloat(l.claim_amount),
          bill_number: l.bill_number || '',
          description: l.description || '',
          proof_file_id: l.proof_file_id || null,
        })),
      }, { withCredentials: true });
      toast.success(`Claim sheet submitted (₹${res.data.sheet.total_amount.toLocaleString('en-IN')}, ${res.data.sheet.line_count} lines)`);
      setShowCreate(false);
      setTrip({ title: '', trip_purpose: '', trip_from: '', trip_to: '', trip_start_date: '', trip_end_date: '' });
      setLines(emptyRows(1));
      setClaimType("direct_reimbursement");
      setSelectedAdvanceId(null);
      await fetchAll();
    } catch (e) {
      toast.error('Submit failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const downloadPdf = async (sheet) => {
    try {
      const res = await axios.get(`${API}/ess/expense-claims/${sheet.id}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense_claim_${sheet.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const fetchSheetDetails = async (sheetId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/ess/expense-claims/${sheetId}`, { withCredentials: true });
      setSelectedSheetDetails(res.data);
    } catch (e) {
      toast.error('Failed to load sheet details: ' + (e?.response?.data?.detail || e.message));
    }
    setLoadingDetails(false);
  };

  const handleToggleExpandRow = async (sheetId) => {
    if (expandedSheet === sheetId) {
      setExpandedSheet(null);
    } else {
      setExpandedSheet(sheetId);
      if (!sheetLinesMap[sheetId]) {
        try {
          const res = await axios.get(`${API}/ess/expense-claims/${sheetId}`, { withCredentials: true });
          setSheetLinesMap(prev => ({ ...prev, [sheetId]: res.data.lines || [] }));
        } catch (e) {
          toast.error('Failed to load sheet lines: ' + (e?.response?.data?.detail || e.message));
        }
      }
    }
  };

  const exportClaimsCSV = () => {
    const headers = ['Sheet No', 'Sheet Title', 'Linked Advance', 'Trip', 'Lines', 'Total Expense', 'Status', 'Submitted Date'];
    const rows = filteredSheets.map(s => [
      s.id?.slice(0, 8) || '—',
      s.title || '—',
      s.advance_id || '—',
      s.trip_from && s.trip_to ? `${s.trip_from} -> ${s.trip_to}` : '—',
      s.line_count || 0,
      s.total_amount || 0,
      s.status?.toUpperCase() || '—',
      s.created_at?.slice(0, 10) || ''
    ]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "my_expense_sheets.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredSheets = useMemo(() => {
    return sheets.filter(s => {
      const matchesSearch = () => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        if (s.title?.toLowerCase().includes(q)) return true;
        if (s.trip_purpose?.toLowerCase().includes(q)) return true;
        if (s.id?.toLowerCase().includes(q)) return true;
        return false;
      };

      const matchesStatus = () => {
        if (statusFilter === 'ALL') return true;
        return s.status === statusFilter;
      };

      const matchesDate = () => {
        if (!s.created_at) return true;
        const subDate = s.created_at.slice(0, 10);
        if (startDate && subDate < startDate) return false;
        if (endDate && subDate > endDate) return false;
        return true;
      };

      return matchesSearch() && matchesStatus() && matchesDate();
    });
  }, [sheets, searchTerm, statusFilter, startDate, endDate]);

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="ess-expense-claims-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-6 h-6 text-blue-600" />
              Expense Claim Sheets
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Submit multi-day trips with multiple expense lines in one go. Each line is policy-checked. Download a print-ready PDF for manual signoff.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ess-new-claim-sheet-btn">
              <Plus className="w-3 h-3 mr-1" /> New Claim Sheet
            </Button>
          </div>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Total Sheets</p>
            <p className="text-xl font-bold text-slate-800 mt-1">{sheets.length}</p>
            <p className="text-[10px] text-slate-400 mt-1">Submitted claim sheets</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider">Pending Sheets</p>
            <p className="text-xl font-bold text-amber-800 mt-1">{sheets.filter(s => s.status === 'pending').length}</p>
            <p className="text-[10px] text-amber-500 mt-1">Awaiting approvals</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Approved Sheets</p>
            <p className="text-xl font-bold text-emerald-800 mt-1">{sheets.filter(s => s.status === 'approved').length}</p>
            <p className="text-[10px] text-emerald-500 mt-1">Finalized claims</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">Total Expense Filed</p>
            <p className="text-xl font-bold text-blue-800 mt-1">
              {fmtINR(sheets.reduce((sum, s) => sum + (s.total_amount || 0), 0))}
            </p>
            <p className="text-[10px] text-blue-500 mt-1">Cumulative trip value</p>
          </div>
        </div>

        {/* Search and Filters Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
            <input 
              type="text"
              placeholder="Search Sheet Title / Purpose..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
          </div>

          <div className="w-[150px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full text-xs px-2 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-600"
            >
              <option value="ALL">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="w-[130px]">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-600"
              title="From Date"
            />
          </div>

          <div className="w-[130px]">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-600"
              title="To Date"
            />
          </div>

          <div className="flex gap-2 ml-auto">
            <button 
              onClick={() => exportClaimsCSV()}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 transition-colors"
              title="Export to CSV"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Export</span>
            </button>
            <button 
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 text-white rounded-lg text-xs hover:bg-slate-700 transition-colors"
              title="Print List"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print</span>
            </button>
            {(searchTerm || statusFilter !== 'ALL' || startDate || endDate) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setStartDate('');
                  setEndDate('');
                }}
                className="px-3 py-2 bg-slate-200 text-slate-700 hover:bg-slate-300 rounded-lg text-xs transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Past sheets list */}
        <Card className="p-0 overflow-hidden border border-slate-200 shadow-sm">
          {filteredSheets.length === 0 ? (
            <p className="text-xs text-slate-500 italic text-center py-12">
              {loading ? 'Loading…' : 'No claim sheets match your criteria.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-slate-600 font-semibold">
                    <th className="p-3 text-left w-10"></th>
                    <th className="text-left p-3">Sheet Title / Purpose</th>
                    <th className="text-left p-3">Linked Advance</th>
                    <th className="text-left p-3">Trip Details</th>
                    <th className="text-right p-3">Total Lines</th>
                    <th className="text-right p-3">Total Expense</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-left p-3">Submitted</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="ess-sheets-table">
                  {filteredSheets.map(s => {
                    const linkedAdvance = openAdvances.find(a => a.advance_id === s.advance_id);
                    const isExpanded = expandedSheet === s.id;
                    const linesCached = sheetLinesMap[s.id] || [];
                    
                    return (
                      <React.Fragment key={s.id}>
                        {/* Main row */}
                        <tr className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`ess-sheet-row-${s.id}`}>
                          <td className="p-3 text-center">
                            <button 
                              onClick={() => handleToggleExpandRow(s.id)}
                              className="text-slate-400 hover:text-slate-600 font-bold transition-transform duration-200"
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="font-semibold text-slate-800 text-[13px]">{s.title}</div>
                            {s.trip_purpose && <div className="text-[11px] text-slate-500 mt-0.5">{s.trip_purpose}</div>}
                            {s.needs_deviation_approval && (
                              <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold">
                                <AlertTriangle className="w-2.5 h-2.5" /> Has deviations
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            {s.advance_id ? (
                              <span className="font-mono text-blue-700 font-bold">
                                {linkedAdvance?.finance_ref || linkedAdvance?.loan_number || s.advance_id.slice(0, 8)}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="p-3 text-slate-600 text-[11px]">
                            {s.trip_from && s.trip_to ? `${s.trip_from} → ${s.trip_to}` : '—'}
                            <div className="text-[10px] text-slate-400 mt-0.5">{s.trip_start_date} → {s.trip_end_date}</div>
                          </td>
                          <td className="p-3 text-right font-mono font-medium">{s.line_count}</td>
                          <td className="p-3 text-right font-mono font-bold text-slate-800">{fmtINR(s.total_amount)}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                              s.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              s.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>{s.status}</span>
                          </td>
                          <td className="p-3 text-slate-500">{(s.created_at || '').slice(0, 10)}</td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-2.5">
                              <button 
                                onClick={() => fetchSheetDetails(s.id)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline text-[11px] font-semibold"
                              >
                                <Eye className="w-3.5 h-3.5" /> Details
                              </button>
                              <button
                                onClick={() => downloadPdf(s)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline text-[11px] font-semibold"
                                data-testid={`ess-sheet-pdf-${s.id}`}
                              >
                                <FileDown className="w-3.5 h-3.5" /> PDF
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Collapsible expanded details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className="bg-slate-50/50 p-4 border-t border-slate-100">
                              <div className="border border-slate-200 rounded-lg bg-white p-3 shadow-inner">
                                <h4 className="text-xs font-bold text-slate-700 mb-2.5 border-b pb-1">Trip Expense Breakdown</h4>
                                {linesCached.length === 0 ? (
                                  <p className="text-xs text-slate-400 text-center py-4 flex items-center justify-center gap-1">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading trip lines...
                                  </p>
                                ) : (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-slate-50 text-slate-550 text-left border-b font-semibold">
                                        <th className="p-2">Date</th>
                                        <th className="p-2">Category</th>
                                        <th className="p-2">Subcategory</th>
                                        <th className="p-2">Bill #</th>
                                        <th className="p-2">Description</th>
                                        <th className="p-2 text-right">Amount</th>
                                        <th className="p-2">Policy Check</th>
                                        <th className="p-2 text-right">Attachment</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {linesCached.map((line, lIdx) => (
                                        <tr key={lIdx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 text-slate-700">
                                          <td className="p-2">{line.bill_date}</td>
                                          <td className="p-2 font-semibold text-slate-800 uppercase">{line.head_code}</td>
                                          <td className="p-2">{line.subcategory || '—'}</td>
                                          <td className="p-2 font-mono">{line.bill_number || '—'}</td>
                                          <td className="p-2 truncate max-w-[200px]" title={line.description}>{line.description || '—'}</td>
                                          <td className="p-2 text-right font-mono font-bold">{fmtINR(line.claim_amount)}</td>
                                          <td className="p-2">
                                            {line.policy_check && (
                                              line.policy_check.needs_deviation_approval ? (
                                                <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-semibold">
                                                  <AlertTriangle className="w-2.5 h-2.5" /> +{fmtINR(line.policy_check.exceeds_by)}
                                                </span>
                                              ) : line.policy_check.has_policy ? (
                                                <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-blue-50 text-blue-800 text-[10px] border border-blue-200 font-semibold">
                                                  Within cap
                                                </span>
                                              ) : (
                                                <span className="text-[10px] text-slate-400">No policy</span>
                                              )
                                            )}
                                          </td>
                                          <td className="p-2 text-right font-semibold">
                                            {line.proof_file_id ? (
                                              <a 
                                                href={`${API}/ess/kyc/proof/${line.proof_file_id}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline flex items-center justify-end gap-1"
                                              >
                                                <Paperclip className="w-3 h-3" /> View Bill
                                              </a>
                                            ) : (
                                              <span className="text-slate-400 font-normal">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* DETAIL PREVIEW MODAL */}
        {selectedSheetDetails && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSheetDetails(null)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-3 mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  Expense Claim Sheet Detail
                </h3>
                <button onClick={() => setSelectedSheetDetails(null)} className="p-1 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Column 1 & 2: Details and Line Items */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Metadata Grid */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs font-semibold">
                    <div>
                      <span className="text-slate-400 block">Sheet Title:</span>
                      <span className="text-slate-800 text-[13px] font-bold">{selectedSheetDetails.title}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Trip Purpose:</span>
                      <span className="text-slate-800">{selectedSheetDetails.trip_purpose || '—'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Trip Route:</span>
                      <span className="text-slate-800">{selectedSheetDetails.trip_from && selectedSheetDetails.trip_to ? `${selectedSheetDetails.trip_from} → ${selectedSheetDetails.trip_to}` : '—'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Trip Dates:</span>
                      <span className="text-slate-800">{selectedSheetDetails.trip_start_date} → {selectedSheetDetails.trip_end_date}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Total Claim Amount:</span>
                      <span className="text-[13px] font-bold text-slate-800">{fmtINR(selectedSheetDetails.total_amount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Linked Advance:</span>
                      <span className="font-mono text-blue-700 font-bold">{selectedSheetDetails.advance_id || 'Direct Claim'}</span>
                    </div>
                  </div>

                  {/* Lines Table */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Expense Items</h4>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b">
                          <tr className="text-slate-600 font-semibold text-left">
                            <th className="p-2.5">Date</th>
                            <th className="p-2.5">Category</th>
                            <th className="p-2.5">Subcategory</th>
                            <th className="p-2.5">Bill #</th>
                            <th className="p-2.5">Description</th>
                            <th className="p-2.5 text-right">Amount</th>
                            <th className="p-2.5 text-right">Proof</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedSheetDetails.lines || []).map((line, lIdx) => (
                            <tr key={lIdx} className="border-b last:border-0 hover:bg-slate-50/50">
                              <td className="p-2.5">{line.bill_date}</td>
                              <td className="p-2.5 font-bold text-slate-755 uppercase">{line.head_code}</td>
                              <td className="p-2.5">{line.subcategory || '—'}</td>
                              <td className="p-2.5 font-mono">{line.bill_number || '—'}</td>
                              <td className="p-2.5 max-w-[150px] truncate" title={line.description}>{line.description || '—'}</td>
                              <td className="p-2.5 text-right font-mono font-bold">{fmtINR(line.claim_amount)}</td>
                              <td className="p-2.5 text-right">
                                {line.proof_file_id ? (
                                  <a 
                                    href={`${API}/ess/kyc/proof/${line.proof_file_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline flex items-center justify-end gap-1 font-semibold"
                                  >
                                    <Paperclip className="w-3 h-3" /> View
                                  </a>
                                ) : (
                                  <span className="text-slate-400 font-normal">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Column 3: Timeline & Settlement */}
                <div className="border-l border-slate-100 pl-4 space-y-6">
                  {/* Settlement / Payment Info */}
                  {selectedSheetDetails.finance_settlement?.status === 'settled' && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-xs text-emerald-800">
                      <h4 className="font-bold mb-2 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        Disbursement / Settlement Completed
                      </h4>
                      <p className="mb-1">UTR/Ref: <span className="font-mono font-bold text-slate-800">{selectedSheetDetails.finance_settlement.utr || '—'}</span></p>
                      <p>Paid Date: <span className="font-bold text-slate-800">{selectedSheetDetails.finance_settlement.settled_at?.slice(0, 10) || '—'}</span></p>
                    </div>
                  )}

                  {/* Vertical Progress History Timeline */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-700 mb-4 uppercase tracking-wide">Approval Workflow History</h4>
                    <div className="relative pl-6 border-l border-slate-200 space-y-6 text-xs ml-3">
                      {/* Step 1: Submission */}
                      <div className="relative">
                        <span className="absolute -left-[31px] top-0.5 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">✓</span>
                        <div className="font-bold text-slate-800">Submitted</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Date: {selectedSheetDetails.created_at?.slice(0, 10)}</div>
                      </div>

                      {/* Step 2: Manager Approval */}
                      <div className="relative">
                        {(() => {
                          const isDone = selectedSheetDetails.manager_approval?.status === 'approved';
                          const isRejected = selectedSheetDetails.manager_approval?.status === 'rejected';
                          return (
                            <>
                              <span className={`absolute -left-[31px] top-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                isDone ? 'bg-emerald-500 text-white' : isRejected ? 'bg-rose-500 text-white' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {isDone ? '✓' : isRejected ? '✗' : '2'}
                              </span>
                              <div className="font-bold text-slate-800">Manager Approval</div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Status: <span className="font-semibold uppercase">{selectedSheetDetails.manager_approval?.status || 'Pending'}</span>
                                {selectedSheetDetails.manager_approval?.approved_at && <div>Date: {selectedSheetDetails.manager_approval.approved_at.slice(0, 10)}</div>}
                                {selectedSheetDetails.manager_approval?.comment && <div className="italic text-slate-400 mt-1">Comment: "{selectedSheetDetails.manager_approval.comment}"</div>}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Step 3: HR Approval */}
                      <div className="relative">
                        {(() => {
                          const isDone = selectedSheetDetails.hr_approval?.status === 'approved';
                          const isRejected = selectedSheetDetails.hr_approval?.status === 'rejected';
                          return (
                            <>
                              <span className={`absolute -left-[31px] top-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                isDone ? 'bg-emerald-500 text-white' : isRejected ? 'bg-rose-500 text-white' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {isDone ? '✓' : isRejected ? '✗' : '3'}
                              </span>
                              <div className="font-bold text-slate-800">HR Approval</div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Status: <span className="font-semibold uppercase">{selectedSheetDetails.hr_approval?.status || 'Pending'}</span>
                                {selectedSheetDetails.hr_approval?.approved_at && <div>Date: {selectedSheetDetails.hr_approval.approved_at.slice(0, 10)}</div>}
                                {selectedSheetDetails.hr_approval?.comment && <div className="italic text-slate-400 mt-1">Comment: "{selectedSheetDetails.hr_approval.comment}"</div>}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Step 4: Finance Settlement */}
                      <div className="relative">
                        {(() => {
                          const isDone = selectedSheetDetails.finance_settlement?.status === 'settled';
                          return (
                            <>
                              <span className={`absolute -left-[31px] top-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                isDone ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                              }`}>
                                {isDone ? '✓' : '4'}
                              </span>
                              <div className="font-bold text-slate-800">Finance Settlement</div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Status: <span className="font-semibold uppercase">{selectedSheetDetails.finance_settlement?.status || 'Pending'}</span>
                                {selectedSheetDetails.finance_settlement?.settled_at && <div>Date: {selectedSheetDetails.finance_settlement.settled_at.slice(0, 10)}</div>}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2 border-t pt-3">
                <button 
                  onClick={() => setSelectedSheetDetails(null)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CREATE MODAL with multi-line grid */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-6xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ess-claim-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-600" /> New Expense Claim Sheet
              </h3>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></button>
            </div>

            {/* Claim Type */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-700">Claim Type *</label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="claimType"
                    value="direct_reimbursement"
                    checked={claimType === "direct_reimbursement"}
                    onChange={() => {
                      setClaimType("direct_reimbursement");
                      setSelectedAdvanceId(null);
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Direct Reimbursement (No Advance)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="claimType"
                    value="against_advance"
                    checked={claimType === "against_advance"}
                    onChange={() => setClaimType("against_advance")}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Against Approved Advance</span>
                </label>
              </div>
            </div>

            {/* Advance Reference if against advance */}
            {claimType === "against_advance" && (
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-700">Advance Reference *</label>
                <select
                  value={selectedAdvanceId || ""}
                  onChange={e => setSelectedAdvanceId(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                >
                  <option value="">Select an advance</option>
                  {openAdvances.map(adv => (
                    <option key={adv.advance_id} value={adv.advance_id}>
                      {adv.finance_ref || adv.loan_number} — {adv.purpose || "No purpose"} — {fmtINR(adv.pending_balance)} available
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Advance Summary if selected */}
            {claimType === "against_advance" && selectedAdvanceId && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Selected Advance Details</h4>
                {(() => {
                  const selected = openAdvances.find(a => a.advance_id === selectedAdvanceId);
                  if (!selected) return null;
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-blue-700 font-medium">Reference:</span>
                        <div>{selected.finance_ref || selected.loan_number}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Purpose:</span>
                        <div>{selected.purpose || "—"}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Finance Paid:</span>
                        <div>{fmtINR(selected.finance_paid_amount)}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Approved Amount:</span>
                        <div>{fmtINR(selected.approved_amount)}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Claim Adjusted:</span>
                        <div>{fmtINR(selected.claim_approved)}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Salary Recovery:</span>
                        <div>{fmtINR(selected.salary_recovery)}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Pending Balance:</span>
                        <div className="font-bold">{fmtINR(selected.pending_balance)}</div>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Settlement Status:</span>
                        <div>{selected.settlement_status}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Trip header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-700">Claim Title *</label>
                <input
                  value={trip.title}
                  onChange={e => setTrip({ ...trip, title: e.target.value })}
                  placeholder='e.g. "Chennai client visit — 22-24 May 2026"'
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                  data-testid="ess-claim-title"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Trip From</label>
                <input
                  value={trip.trip_from}
                  onChange={e => setTrip({ ...trip, trip_from: e.target.value })}
                  placeholder="Bangalore"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Trip To</label>
                <input
                  value={trip.trip_to}
                  onChange={e => setTrip({ ...trip, trip_to: e.target.value })}
                  placeholder="Chennai"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Start Date</label>
                <input type="date" value={trip.trip_start_date} onChange={e => setTrip({ ...trip, trip_start_date: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">End Date</label>
                <input type="date" value={trip.trip_end_date} onChange={e => setTrip({ ...trip, trip_end_date: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-700">Trip Purpose</label>
                <input
                  value={trip.trip_purpose}
                  onChange={e => setTrip({ ...trip, trip_purpose: e.target.value })}
                  placeholder="Q3 client review meeting"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                />
              </div>
            </div>

              {/* Lines grid */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Expense Lines ({lines.length})</h4>
                <Button variant="outline" size="sm" onClick={addRow} data-testid="ess-claim-add-row">
                  <Plus className="w-3 h-3 mr-1" /> Add Row
                </Button>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2 text-left w-[110px]">Date</th>
                      <th className="p-2 text-left w-[170px]">Category</th>
                      <th className="p-2 text-left w-[120px]">Subcategory</th>
                      <th className="p-2 text-right w-[100px]">Amount ₹</th>
                      <th className="p-2 text-left w-[90px]">Bill #</th>
                      <th className="p-2 text-left">Description</th>
                      <th className="p-2 text-left w-[150px]">Policy / Proof</th>
                      <th className="p-1"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="ess-claim-lines-table">
                    {lines.map((line, idx) => {
                      const pol = policyForLine(line);
                      return (
                        <tr key={idx} className="border-t border-slate-100" data-testid={`ess-claim-line-${idx}`}>
                          <td className="p-1"><input type="date" value={line.line_date} onChange={e => updateLine(idx, { line_date: e.target.value })} className="w-full border border-slate-300 rounded px-1 py-1" data-testid={`ess-claim-line-date-${idx}`} /></td>
                          <td className="p-1">
                            <select value={line.head_code} onChange={e => updateLine(idx, { head_code: e.target.value })} className="w-full border border-slate-300 rounded px-1 py-1" data-testid={`ess-claim-line-head-${idx}`}>
                              <option value="">— pick —</option>
                              {headOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td className="p-1"><input value={line.subcategory} onChange={e => updateLine(idx, { subcategory: e.target.value })} placeholder="(opt)" className="w-full border border-slate-300 rounded px-1 py-1" /></td>
                          <td className="p-1"><input type="number" value={line.claim_amount} onChange={e => updateLine(idx, { claim_amount: e.target.value })} className="w-full border border-slate-300 rounded px-1 py-1 text-right font-mono" data-testid={`ess-claim-line-amt-${idx}`} /></td>
                          <td className="p-1"><input value={line.bill_number} onChange={e => updateLine(idx, { bill_number: e.target.value })} className="w-full border border-slate-300 rounded px-1 py-1" /></td>
                          <td className="p-1"><input value={line.description} onChange={e => updateLine(idx, { description: e.target.value })} className="w-full border border-slate-300 rounded px-1 py-1" /></td>
                          <td className="p-1">
                            <div className="space-y-1">
                              {pol && parseFloat(line.claim_amount) > 0 && (
                                <div data-testid={`ess-claim-line-policy-${idx}`}>
                                  {pol.autoApprove ? (
                                    <span className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 bg-emerald-100 text-emerald-800 text-[10px]"><CheckCircle2 className="w-3 h-3" /> Auto-approve</span>
                                  ) : pol.within ? (
                                    <span className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 bg-blue-50 text-blue-800 text-[10px] border border-blue-200"><CheckCircle2 className="w-3 h-3" /> Within ₹{pol.cap.toLocaleString('en-IN')}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 bg-rose-100 text-rose-800 text-[10px]" data-testid={`ess-claim-line-exceeds-${idx}`}><AlertTriangle className="w-3 h-3" /> +₹{pol.exceedsBy.toLocaleString('en-IN')}</span>
                                  )}
                                </div>
                              )}
                              <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-blue-600 hover:underline">
                                <Upload className="w-3 h-3" />
                                {line.proof_filename ? <span className="truncate max-w-[80px]">{line.proof_filename}</span> : 'Attach'}
                                <input
                                  type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                                  onChange={e => e.target.files?.[0] && uploadProof(idx, e.target.files[0])}
                                  data-testid={`ess-claim-line-proof-${idx}`}
                                />
                              </label>
                              {line.proof_file_id && <FileText className="w-3 h-3 text-emerald-600 inline" />}
                            </div>
                          </td>
                          <td className="p-1 text-right">
                            {lines.length > 1 && (
                              <button onClick={() => removeRow(idx)} className="text-rose-500 hover:text-rose-700" data-testid={`ess-claim-line-remove-${idx}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                      <td colSpan={3} className="p-2 text-right">Sheet Total</td>
                      <td className="p-2 text-right font-mono text-base" data-testid="ess-claim-total">₹{total.toLocaleString('en-IN')}</td>
                      <td colSpan={4} className="p-2">
                        {anyDeviation && (
                          <span className="inline-flex items-center gap-1 text-rose-700 text-[11px]"><AlertTriangle className="w-3 h-3" /> One or more lines exceed policy — special approval required.</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button
                  onClick={submitSheet}
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="ess-claim-submit"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : `Submit Sheet (₹${total.toLocaleString('en-IN')})`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
